import {AU, Loader, NZ, useAnalytics, useLocalStorage, Viewer} from 'bigdatr-style';
import {SupportedCountry} from 'bigdatr-style';
import {createContext, useContext, useEffect, useState, useMemo} from 'react';
import React from 'react';
import {useLocation} from 'react-router-dom';
import getSafeCountry from './getSafeCountry';
import {useRoutes} from '~/app/Router';
import rqApi from '~/api';

type CountryContextValue = {
    country: SupportedCountry;
    setCountry: (next: SupportedCountry) => void;
    isCountryOnTrial: boolean;
    isMediaValueOnTrial: boolean;
    isCreativeOnTrial: boolean;
    isComplianceOnTrial: boolean;
    hasMediaValue: boolean;
    hasCreative: boolean;
};
const CountryContext = createContext<CountryContextValue | undefined>(undefined);

export function CountryProvider(props: {children: React.ReactNode; viewer: Viewer}) {
    const {viewer} = props;
    const updateMessage = rqApi.useUserUpdateSession();
    const routes = useRoutes();
    const analytics = useAnalytics();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [hasCountryInUrl, setHasCountryInUrl] = useState<boolean>(searchParams.has('country'));
    const [showLoader, setShowLoader] = useState(hasCountryInUrl);

    const [countryLocalStorage, setCountryLocalStorage] =
        useLocalStorage<SupportedCountry>('bd-app-lastCountry');

    const setCountry = async (next: SupportedCountry, redirectToHomepage = true) => {
        setShowLoader(true);
        updateMessage.mutate(
            {
                id: viewer.user.id,
                sessionMetadata: {country: next}
            },
            {
                onError: (error) => {
                    analytics.trackError(error);
                }
            }
        );

        setCountryLocalStorage(next);

        if (redirectToHomepage) {
            // go to explore creative page on country switch
            routes.exploreCreative.push((prev) => {
                // if the next country is the same, keep url params
                if (next === countryLocalStorage) return {...prev};
                // otherwise purge them
                return {};
            });
        }

        // this isn't the best, however showing a loader resolver lots of rendering issues that
        // happen when we switch countries. for example, if you are on explore creatives page and
        // have some filters selected, the business wants these filters to be resetted after
        // switching countries. the URL updates correctly and has a reset state, but the UI does not
        // reflect this update. it is possible to force a re-render on the page but this could have
        // unintended side effects.
        //
        // as a result, i opted to show a loader for 1 second any time the country is changed which
        // forces the app to un-mount and re-mount. code wise this isn't amazing, however UX wise i
        // think its common for a page to refresh after changing a language on a website (which is
        // kinda what the country switch does)
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setShowLoader(false);
    };

    const {hasAccess, country: lastSafeCountry} = getSafeCountry(viewer);

    // handles redirects if the user has no access to the app
    useEffect(() => {
        // don't force any redirects if user has access
        if (hasAccess) return;

        const noAccessAllowedRoutes = [
            routes.trialExpired.to(),
            routes.noPermissions.to(),
            '/creative'
        ];

        const isOnNoAccessRoute = noAccessAllowedRoutes.some((path) =>
            location.pathname.startsWith(path)
        );

        // dont force any redirects if the user is already on a route that no access users are
        // allowed to see
        if (isOnNoAccessRoute) return;

        const {currentTeam} = viewer;
        if (currentTeam.hasNoPermissions) routes.noPermissions.replace();
        if (currentTeam.isAdminOnlyTeam && currentTeam.hasTeamAdmin)
            routes.userAdminTeamList.replace({});
        if (currentTeam.trialExpired) routes.trialExpired.replace();
    }, [hasAccess, location.pathname, viewer.currentTeamId]);

    // this will update local storage with whatever the API says is true. this is needed in case
    // local storage value says NZ, but viewer data returns AU. use cases of this being important is
    // if customers stopped paying for NZ, but in their local storage NZ was the last used country.
    // when this happens we should update the local storage value to AU
    useEffect(() => {
        if (hasAccess && lastSafeCountry !== countryLocalStorage) {
            setCountryLocalStorage(lastSafeCountry);
        }
    }, [lastSafeCountry]);

    // force country selection based on URL
    useEffect(() => {
        if (!hasCountryInUrl) {
            return;
        }

        const countryFromUrl = searchParams.get('country') as SupportedCountry;

        if (!viewer.currentTeam.countriesWithAccess.includes(countryFromUrl)) {
            setHasCountryInUrl(false);
            setShowLoader(false);
            return;
        }

        // update local storage and user session
        setCountry(countryFromUrl, false);
        setHasCountryInUrl(false);
    }, []);

    const country = countryLocalStorage ?? lastSafeCountry;
    const isCountryOnTrial = viewer.isCountryOnTrial(country);

    const isMediaValueOnTrial = viewer.isProductOnTrial('MediaValue', country);
    const isCreativeOnTrial = viewer.isProductOnTrial('AdvertisingCreative', country);
    const isComplianceOnTrial = viewer.isProductOnTrial('Compliance', country);
    const hasMediaValue =
        (viewer.currentTeam.hasAusMediaValue && country === AU) ||
        (viewer.currentTeam.hasNzMediaValue && country === NZ);
    const hasCreative =
        (viewer.currentTeam.hasAusCreative && country === AU) ||
        (viewer.currentTeam.hasNzCreative && country === NZ);

    const data: CountryContextValue = useMemo(() => {
        return {
            country,
            setCountry,
            isCountryOnTrial,
            isMediaValueOnTrial,
            isCreativeOnTrial,
            isComplianceOnTrial,
            hasMediaValue,
            hasCreative
        };
    }, [
        country,
        isCountryOnTrial,
        isMediaValueOnTrial,
        isCreativeOnTrial,
        hasMediaValue,
        isComplianceOnTrial,
        hasCreative
    ]);

    return (
        <CountryContext.Provider value={data}>
            {showLoader ? <Loader /> : props.children}
        </CountryContext.Provider>
    );
}

export function useCountry() {
    const context = useContext(CountryContext);
    if (!context) throw new Error('CountryContext is not yet initialized');
    return context;
}

/** Should only be used in places where country context might not be initialised, eg on public creative routes */
export function useCountryUnsafe() {
    const context = useContext(CountryContext);
    return context;
}
